"use client";

/**
 * Client body for the support-to-engineering remediation use-case page. Lives
 * apart from `page.tsx` (which stays a server component for `metadata`) so it
 * can call `useT()` and follow the visitor's locale, exactly like the homepage.
 * All copy comes from the `ucRemediation` namespace; structure (icons, hrefs)
 * stays here.
 */

import {
  Inbox,
  Ticket,
  Search,
  GitPullRequest,
  ShieldCheck,
  FlaskConical,
  Lock,
  History,
} from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  UseCaseHero,
  ProductShot,
  Section,
  SectionHeader,
  WorkflowSteps,
  FeatureGrid,
  Faq,
  SecurityNote,
  RelatedLinks,
  CtaBanner,
} from "@/components/marketing/marketing-kit";
import { useT } from "@/i18n";

export function RemediationContent() {
  const t = useT();
  const k = (s: string) => `ucRemediation.${s}`;

  const steps = [
    { key: "report", icon: <Inbox size={18} /> },
    { key: "mission", icon: <Search size={18} /> },
    { key: "patch", icon: <GitPullRequest size={18} /> },
    { key: "checks", icon: <FlaskConical size={18} /> },
    { key: "gates", icon: <ShieldCheck size={18} /> },
  ].map((s) => ({
    icon: s.icon,
    tag: t(k(`steps.${s.key}.tag`)),
    title: t(k(`steps.${s.key}.title`)),
    body: t(k(`steps.${s.key}.body`)),
  }));

  const features = [
    { key: "ticket", icon: <Ticket size={18} /> },
    { key: "together", icon: <Search size={18} /> },
    { key: "diff", icon: <GitPullRequest size={18} /> },
    { key: "evidence", icon: <FlaskConical size={18} /> },
    { key: "gates", icon: <ShieldCheck size={18} /> },
    { key: "timeline", icon: <History size={18} /> },
  ].map((f) => ({
    icon: f.icon,
    title: t(k(`features.${f.key}.title`)),
    body: t(k(`features.${f.key}.body`)),
  }));

  const faq = ["q1", "q2", "q3", "q4", "q5", "q6"].map((q) => ({
    q: t(k(`faq.${q}.q`)),
    a: t(k(`faq.${q}.a`)),
  }));

  return (
    <MarketingShell>
      <UseCaseHero
        eyebrow={t(k("hero.eyebrow"))}
        title={t(k("hero.title"))}
        highlight={t(k("hero.highlight"))}
        subtitle={t(k("hero.subtitle"))}
        primary={{ href: "/demo", label: t(k("hero.primaryCta")) }}
        secondary={{ href: "/contact", label: t(k("hero.secondaryCta")) }}
        bullets={[t(k("hero.bullet1")), t(k("hero.bullet2")), t(k("hero.bullet3"))]}
      />

      <ProductShot src="/screenshots/workspace.png" alt={t(k("shot.alt"))} label={t(k("shot.label"))} />

      <Section>
        <SectionHeader
          eyebrow={t(k("how.eyebrow"))}
          title={t(k("how.title"))}
          description={t(k("how.description"))}
        />
        <WorkflowSteps steps={steps} />
      </Section>

      <Section muted>
        <SectionHeader
          eyebrow={t(k("features.eyebrow"))}
          title={t(k("features.title"))}
          description={t(k("features.description"))}
        />
        <FeatureGrid features={features} />
      </Section>

      <Section>
        <SectionHeader eyebrow={t(k("governed.eyebrow"))} title={t(k("governed.title"))} />
        <SecurityNote icon={<Lock size={18} />} title={t(k("governed.noteTitle"))}>
          {t(k("governed.noteBody"))}
        </SecurityNote>
      </Section>

      <Section muted>
        <SectionHeader eyebrow={t(k("faq.eyebrow"))} title={t(k("faq.title"))} />
        <Faq items={faq} />
      </Section>

      <Section>
        <RelatedLinks
          title={t("mk.relatedTitle")}
          links={[
            {
              href: "/use-cases/ai-agent-approval-ledger",
              label: t(k("related.approvalLedger.label")),
              description: t(k("related.approvalLedger.description")),
            },
            {
              href: "/use-cases/ai-deployment-governance",
              label: t(k("related.deployment.label")),
              description: t(k("related.deployment.description")),
            },
            {
              href: "/integrations/github",
              label: t(k("related.github.label")),
              description: t(k("related.github.description")),
            },
          ]}
        />
      </Section>

      <CtaBanner
        title={t(k("cta.title"))}
        description={t(k("cta.description"))}
        primary={{ href: "/demo", label: t(k("cta.primary")) }}
        secondary={{ href: "/contact", label: t(k("cta.secondary")) }}
      />
    </MarketingShell>
  );
}
